import React, { createContext, useContext, useReducer, useEffect } from 'react';

const initialState = {
  darkMode: localStorage.getItem('darkMode') === '1' || false,
  fontSizeDelta: 0, // relative adjustments
  language: localStorage.getItem('i18nextLng') || 'mr',
  drawerOpen: false,
  selectedNavKey: '/',
  notifications: [],
  clientId: localStorage.getItem('clientId') || null,
};

const AppContext = createContext(null);

function reducer(state, action) {
  switch (action.type) {
    case 'TOGGLE_DARK':
      return { ...state, darkMode: !state.darkMode };
    case 'SET_DARK':
      return { ...state, darkMode: !!action.payload };
    case 'INCREASE_FONT':
      return { ...state, fontSizeDelta: state.fontSizeDelta + 1 };
    case 'DECREASE_FONT':
      return { ...state, fontSizeDelta: Math.max(-5, state.fontSizeDelta - 1) };
    case 'SET_LANGUAGE':
      if (action.payload) localStorage.setItem('i18nextLng', action.payload);
      return { ...state, language: action.payload };
    case 'SET_DRAWER':
      return { ...state, drawerOpen: !!action.payload };
    case 'SET_SELECTED_NAV':
      return { ...state, selectedNavKey: action.payload };
    case 'PUSH_NOTIFICATION':
      return { ...state, notifications: [...state.notifications, action.payload] };
    case 'SET_CLIENT_ID':
      if (action.payload) localStorage.setItem('clientId', action.payload);
      return { ...state, clientId: action.payload };
    default:
      return state;
  }
}

export const AppProvider = ({ children, initial = {} }) => {
  const merged = { ...initialState, ...initial };
  const [state, dispatch] = useReducer(reducer, merged);

  // Apply title-only scale via a CSS variable when fontSizeDelta changes
  useEffect(() => {
    try {
      const delta = Number(state.fontSizeDelta) || 0;
      // map delta to a reasonable scale factor (0.8 - 1.5)
      const scale = Math.min(1.5, Math.max(0.8, 1 + delta * 0.08));
      document.documentElement.style.setProperty(
        '--app-title-scale',
        String(scale)
      );
    } catch (e) {
      // noop
    }
  }, [state.fontSizeDelta]);

  // Sync dark mode to body class and persist preference
  useEffect(() => {
    try {
      if (state.darkMode) {
        document.body.classList.add('dark-mode');
      } else {
        document.body.classList.remove('dark-mode');
      }
      localStorage.setItem('darkMode', state.darkMode ? '1' : '0');
    } catch (e) {
      // noop
    }
  }, [state.darkMode]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

// Non-throwing hook: returns context or null when provider is absent
export const useAppContext = () => {
  const ctx = useContext(AppContext);
  return ctx || null;
};

// Strict hook: throws when provider is missing (use this when provider is required)
export const useStrictAppContext = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useStrictAppContext must be used within AppProvider');
  return ctx;
};

export default AppContext;
